import { Icon, Spinner, SpinnerSize, Tab } from "@blueprintjs/core";
import { useApiEffect } from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";

import { ERequestState, ERequestType, RequestApi, WorkAttestationRequestApi } from "../../../../api";
import {
  BackButton,
  ConditionTab,
  LinkedSubFilesTab,
  PageBase,
  ProtectedTabs,
  RequestHistoryTab,
  RequestStickyMenu,
  VerificationTab,
} from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { MailLogListPage } from "../../../mail-log/MailLogListPage";
import { BillTab } from "../../BillTab";
import { WorkAttestationRequestDetailPage } from "./WorkAttestationRequestDetailPage";

export interface IWorkAttestationRequestItemPageProps {}

export const WorkAttestationRequestItemPage: React.FunctionComponent<IWorkAttestationRequestItemPageProps> = (
  props,
) => {
  const {
    id,
    tab,
    state,
    masterRequestId = 0,
  } = useParams<{
    id: string;
    tab: string;
    state: string;
    masterRequestId: string;
  }>();
  const history = useHistory();
  const api = useApiService(WorkAttestationRequestApi);
  const requestApi = useApiService(RequestApi);
  const { currentLanguage } = useGlobalData();
  const { t } = useTl();

  const [requestInfo, requestInfoLoading, , refreshRequestInfo] = useApiEffect(
    () => requestApi.getRequestInfo(ERequestType.WorkAttestRequest, +id, +masterRequestId, currentLanguage.languageId),
    [requestApi, id, masterRequestId, currentLanguage],
  );

  const refreshRequest = React.useCallback(() => {
    refreshRequestInfo();
  }, [refreshRequestInfo]);

  const closeRequest = React.useCallback(() => {
    api.closeRequest(requestInfo?.parentRequestId);
  }, [api, requestInfo]);

  return (
    <PageBase
      breadCrumbs={[
        {
          text: t(ETLCodes.WorkAttestationRequests),
          route: ERoutes.workAttestationRequest,
        },
        {
          text:
            +id > 0 ? (
              requestInfoLoading ? (
                <Spinner size={SpinnerSize.SMALL} />
              ) : (
                requestInfo.requestNumber
              )
            ) : (
              t(ETLCodes.New)
            ),
        },
      ]}
      actionHistory={
        +id > 0 &&
        !requestInfoLoading && {
          tlCode: ETLCodes.ActionHistoryWorkAttestationRequest,
          name: requestInfo.requestNumber,
          id: +id,
        }
      }
      stickyContent={() => {
        if (+id > 0 && !requestInfoLoading) {
          return (
            <RequestStickyMenu
              requestId={requestInfo.parentRequestId}
              refreshFunc={refreshRequest}
              closeFunc={closeRequest}
            />
          );
        } else {
          return null;
        }
      }}
    >
      <ProtectedTabs
        id="TabsWorkAttestRequest"
        onChange={(newTabId) => history.push(`${ERoutes.workAttestationRequest}/${+id}/${newTabId}/${state}`)}
        selectedTabId={tab}
        renderActiveTabPanelOnly
      >
        <BackButton backRoute={ERoutes.workAttestationRequest} />
        <Tab
          id="detail"
          title={
            <div>
              {t(ETLCodes.Detail)} {requestInfo?.hasWarning && <Icon icon="warning-sign" intent="danger" />}
            </div>
          }
          panel={<WorkAttestationRequestDetailPage workAttestationRequestId={+id} editMode={state === "edit"} />}
        />
        <Tab
          id="verification"
          title={t(ETLCodes.Verifications)}
          panel={
            requestInfoLoading ? (
              <Spinner />
            ) : (
              <VerificationTab
                requestId={requestInfo.parentRequestId}
                editMode={state === "edit"}
                statusETLCode={ETLCodes.VerificationsToObtainBilledStatus}
              />
            )
          }
          disabled={+id < 1}
        />
        <Tab
          id="conditions"
          title={t(ETLCodes.Conditions)}
          panel={
            requestInfoLoading ? (
              <Spinner />
            ) : (
              <ConditionTab
                requestId={requestInfo?.parentRequestId}
                editMode={state === "edit"}
                statusETLCode={ETLCodes.ConditionsForBillingStatus}
                requestState={requestInfo?.requestStateCode}
              />
            )
          }
          disabled={+id <= 0}
        />
        <Tab
          id="subrequests"
          title={t(ETLCodes.SubRequests, {
            count: requestInfoLoading ? `` : `(${requestInfo.subRequestCount})`,
          })}
          panel={
            requestInfoLoading ? (
              <Spinner />
            ) : (
              <LinkedSubFilesTab requestInfo={requestInfo} editMode={state === "edit"} />
            )
          }
          disabled
        />
        <Tab
          id="billing"
          title={t(ETLCodes.Billing)}
          panel={
            <BillTab
              requestId={requestInfo?.parentRequestId}
              disabled={requestInfo?.requestStateCode !== ERequestState.CANCELLED}
            />
          }
          disabled={requestInfo?.requestStateCode === ERequestState.REQUESTED}
        />
        <Tab
          id="history"
          title={t(ETLCodes.History)}
          panel={requestInfoLoading ? <Spinner /> : <RequestHistoryTab requestId={requestInfo.parentRequestId} />}
          disabled={+id <= 0}
        />
        <Tab
          id="mailing"
          title={t(ETLCodes.Email)}
          panel={requestInfoLoading ? <Spinner /> : <MailLogListPage requestId={requestInfo?.parentRequestId} />}
          disabled={+id <= 0}
        />
      </ProtectedTabs>
    </PageBase>
  );
};
